import type ManageRolloverWizardRoute from 'teacher-ui-client/manage/rollover/wizard/route'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { t, type IntlService } from 'ember-intl'
import { RolloverIndexPage } from 'teacher-ui-client/components/page/rollover'
import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import type SessionService from 'teacher-ui-client/services/session'
import { service } from '@ember/service'
import { LinkTo } from '@ember/routing'
import canAccess from 'teacher-ui-client/helpers/can-access'
import { AMER, EMEA } from 'teacher-ui-client/feature-control/marketing-regions'
import {
  InlineAlert,
  AlertType,
  InlineAlertTitle,
  InlineAlertSubtitle,
  InlineAlertActionsContainer,
} from 'teacher-ui-client/components/inline-alert'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'

interface Signature {
  Args: {
    model: ModelFor<ManageRolloverWizardRoute>
  }
}

export class ManageRolloverWizardPage extends Component<Signature> {
  @service session!: SessionService

  get schoolName() {
    return this.session.currentSchool.name
  }

  <template>
    <PageHeader>
      <:title>
        <h1>
          {{t "manage.rollover.title"}}
        </h1>
      </:title>
    </PageHeader>
    {{#if (canAccess "one_roster")}}
      <ManageRolloverOneRosterRolloutPage
        @schoolName={{this.schoolName}}
        @schoolCountryCode={{this.session.currentSchool.countryCode}}
        @subscriptionType={{@model.subscriptionType}}
      />
    {{else}}
      <RolloverIndexPage @schoolSubscriptions={{@model.schoolSubscriptions}} />
    {{/if}}
  </template>
}

export default RouteTemplate(ManageRolloverWizardPage)

interface OneRosterPageSignature {
  Args: {
    schoolName: string
    schoolCountryCode: string
    subscriptionType: SubscriptionType
  }
}

export class ManageRolloverOneRosterRolloutPage extends Component<OneRosterPageSignature> {
  @service intl!: IntlService

  get productVideoDetails() {
    if (AMER.includes(this.args.schoolCountryCode)) {
      return null
    }

    return {
      src: 'https://www.youtube.com/embed/_DeYtucXmrs',
      title: this.intl.t('manage.3essentials.videoTitle'),
    }
  }

  get csEmail() {
    if (EMEA.includes(this.args.schoolCountryCode)) {
      return 'support@3plearning.co.uk'
    }

    switch (this.args.schoolCountryCode) {
      case 'US':
        return 'support.usa@3plearning.com'
      case 'CA':
        return 'customerservice@3plearning.ca'
      case 'AU':
        return 'customerservice@3plearning.com.au'
      default:
        return 'customerservice@3plearning.com.au'
    }
  }

  <template>
    <div class="flex flex-wrap items-stretch gap-6 xl:flex-nowrap">

      <InlineAlert data-test-roster-alert @type={{AlertType.Warning}}>
        <InlineAlertTitle>
          {{t "manage.rollover.oneRoster.alertTitle"}}
        </InlineAlertTitle>
        <InlineAlertSubtitle class="space-y-2">
          <p>
            {{t "manage.rollover.oneRoster.blurb"}}
          </p>
          <p>
            {{t "manage.rollover.oneRoster.contactUsEmail" email=this.csEmail schoolName=@schoolName htmlSafe=true}}
          </p>
        </InlineAlertSubtitle>
        <InlineAlertActionsContainer>
          <LinkTo @route="subscription-type.contact-us" class="text-blue-325 hover:text-blue-350">
            {{t "manage.rollover.oneRoster.contactUs"}}
            <PhosphorIcon @icon="phosphor-arrow-right-bold" />
          </LinkTo>
        </InlineAlertActionsContainer>
      </InlineAlert>
      {{#if this.productVideoDetails}}
        <div class="aspect-video w-108 shrink-0 shadow-xl">
          <iframe
            data-test-roster-alert-video
            class="snappy:invisible h-full w-full rounded-sm object-cover"
            src={{this.productVideoDetails.src}}
            title={{this.productVideoDetails.title}}
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
          >
          </iframe>
        </div>
      {{/if}}
    </div>
  </template>
}
